import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import "./SessionTimeoutWarning.css";

interface SessionTimeoutWarningProps {
  warningMinutes?: number; // Quantos minutos antes da expiração mostrar o aviso
}

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
};

const SessionTimeoutWarning: React.FC<SessionTimeoutWarningProps> = ({
  warningMinutes = 10,
}) => {
  const { logout, expiresAt, isAuthenticated } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);

  useEffect(() => {
    const checkTokenExpiration = () => {
      if (!isAuthenticated || !expiresAt) {
        if (showWarning) setShowWarning(false);
        return;
      }

      const remaining = Math.max(0, Math.floor((Date.parse(expiresAt) - Date.now()) / 1000));
      const remainingMinutes = remaining / 60;

      // Só atualiza se o valor realmente mudou (evita re-renders desnecessários)
      setTimeRemaining((prev) => (prev !== remaining ? remaining : prev));

      // Mostrar aviso se estiver dentro do período de aviso
      const shouldShow =
        remainingMinutes <= warningMinutes && remainingMinutes > 0;
      if (showWarning !== shouldShow) {
        setShowWarning(shouldShow);
      }
    };

    // Verificar imediatamente
    checkTokenExpiration();

    // Verificar a cada 10 segundos
    const interval = setInterval(checkTokenExpiration, 10000);

    return () => clearInterval(interval);
  }, [warningMinutes, showWarning, expiresAt, isAuthenticated]);

  const handleContinue = () => {
    setShowWarning(false);
    // Aqui você poderia implementar uma lógica para renovar o token
    // fazendo uma chamada ao backend
  };

  const handleLogout = async () => {
    try { await logout(); window.location.href = "/login"; }
    catch { window.alert("Não foi possível sair. Tente novamente."); }
  };

  if (!showWarning) {
    return null;
  }

  return (
    <div className="session-timeout-overlay">
      <div className="session-timeout-modal">
        <div className="session-timeout-icon">⚠️</div>
        <h3>Sessão Expirando</h3>
        <p>
          Sua sessão está prestes a expirar em{" "}
          <strong>{formatTime(timeRemaining)}</strong>
        </p>
        <p className="session-timeout-message">
          Você será desconectado automaticamente quando o tempo acabar.
        </p>
        <div className="session-timeout-actions">
          <button className="btn btn-secondary" onClick={handleLogout}>
            Sair Agora
          </button>
          <button className="btn btn-primary" onClick={handleContinue}>
            Continuar Trabalhando
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutWarning;
